<template>
  <div class="cart-items flex-stretch">
    <div class="empty-header"></div>
    <div class="header header-item first">Item</div>
    <div class="header header-price">Item Price</div>
    <div class="header header-quantity">Quantity</div>
    <div class="header header-total">Total Price</div>
    <template v-for="item in cartItems">
      <div class="image-cell">
        <ThumbnailedImage
          v-if="item.product?.images"
          :images="item.product.images[0]"
          :alt="item.product.name"
          sizes="150px"
        />
      </div>
      <div class="meta-cell flex-column flex-space-between second">
        <div v-if="item.promotion && item.product">
          <div>
            <em>{{ promoName(item.promotion) }}</em>
          </div>
          <div class="name">{{ item.product.name }}</div>
          <div class="remoteId">{{ item.product.remote_id }}</div>
        </div>
        <div v-else-if="item.product">
          <RouterLink
            :to="{
              name: 'product-detail',
              params: { slug: item.product.slug },
            }"
            class="name"
            tabindex="-1"
          >
            {{ shop.getProductNameWithoutColor(item.product.name) }}
          </RouterLink>
          <div class="remoteId">{{ item.product.remote_id }}</div>
        </div>
        <div v-else>Item</div>

        <div v-if="item.product">
          <div>
            {{ shop.getProductColorNames(item.product).join(", ") }}
          </div>
          <div>
            {{ shop.getProductType(item.product) }}
          </div>
        </div>

        <div v-if="item.extras" class="extra-form">
          <div v-if="item.extras.eventforms" class="eventforms">
            <div
              v-for="(values, eventform) in item.extras.eventforms"
              class="eventform p-2xs-y"
            >
              <div v-for="(value, key) in values" class="eventform-row">
                {{ `${key}`.replace("_", " ") }}: {{ value }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="price-cell pa-p1">
        <ProductPrice v-if="!item.promotion" :object="item.product" />
      </div>
      <div class="quantity-cell pa-p1">
        <div
          v-if="item.extras"
          class="flex-column flex-center flex-align-center p-2xs-t"
        >
          1
        </div>
        <div
          v-else-if="item.promotion"
          class="flex-column flex-center flex-align-center p-2xs-t"
        >
          {{ item.quantity }}
        </div>
        <NumQuantity
          v-else-if="!item.promotion"
          v-model="item.quantity"
          @update:modelValue="checkQuantity(item)"
        />
        <a
          href="#"
          v-if="!item.promotion"
          tabindex="-1"
          @click.prevent="removeItem(item)"
          class="small link remove-button"
        >
          Remove
        </a>
      </div>
      <div class="total-cell pa-p1">
        <ProductPrice :object="item.product" :quantity="item.quantity" />
        <template v-if="props.showWishlist">
          <a
            href="#"
            tabindex="-1"
            v-if="!item.promotion"
            @click.prevent="moveToWishlist(item)"
            class="small"
          >
            Add to Wishlist
          </a>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useCartStore } from "@/stores/cart";
import { useJoinStore } from "@/stores/join";
import { useShopStore } from "@/stores/shop";
import {
  checkQuantity,
  moveToWishlist,
  removeItem,
} from "@/stores/utils/add_to_cart";
import { computed } from "vue";
import ThumbnailedImage from "../ThumbnailedImage.vue";
import NumQuantity from "./NumQuantity.vue";
import ProductPrice from "./ProductPrice.vue";

const props = withDefaults(
  defineProps<{
    showWishlist?: boolean;
    showPromoItems?: boolean;
  }>(),
  {
    showWishlist: true,
    showPromoItems: true,
  },
);

const join = useJoinStore();
const cart = useCartStore();
const shop = useShopStore();

const cartItems = computed(() => {
  if (!props.showPromoItems) {
    return cart.items.filter((i) => !i.promotion);
  }
  return cart.items;
});

function promoName(promoId: number) {
  const promo = shop.objects.promos.find((p) => p.id === promoId);
  return promo?.description || "Promotion";
}
</script>

<style scoped>
.header {
  border-bottom: 1px solid var(--30, #c4c4c4);
  padding-bottom: 5px;
}
.first {
  padding-left: var(--Sm, 24px);
}
.second {
  padding-left: var(--lg, 48px);
}

.cart-items {
  display: grid;
  grid-template-columns: auto 1fr repeat(2, minmax(100px, auto)) auto;
  gap: var(--3XS, 4px) 0;
}

.image-cell {
  margin-right: var(--md);
}
.image-cell img {
  object-fit: contain;
  width: 150px;
  height: 188px;
}
.meta-cell {
  flex: 1 1 auto;
  margin: 0 var(--Sm);
  gap: var(--XMd);
}
.meta-cell .name {
  /*margin-top: var(--XS);*/
  margin-bottom: var(--2XS);
}
.meta-cell .remoteId {
  color: var(--50, #939598);
}
#app .quantity-cell {
  padding-top: 7px;
}

.quantity-cell .flex-row {
  margin: 0 var(--Button-Spacing-Large, 10px) 0 0;
  padding: 0 0 var(--2XS, 8px) 0px;
  gap: var(--2XS, 8px);
  color: #000;
  text-align: center;
}
.quantity-cell .flex-row :deep(.p-button-icon) {
  font-size: 6px;
}

.p-button {
  padding: var(--2XS, 8px);
}
.p-button.pink,
.p-button.grey {
  padding: var(--2XS, 8px) var(--XS, 16px);
}

#app .p-button-text:hover {
  color: var(--Black, #000);
  font-weight: 400;
}
.meta-cell,
.price-cell,
.quantity-cell,
.total-cell {
  margin: var(--md) 0 0 0;
  padding-top: var(--XS);
  padding-bottom: var(--XMd);
  border-bottom: 1px solid var(--30, #c4c4c4);
}
.quantity-cell,
.total-cell {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.price-cell,
.quantity-cell {
  padding-right: 16px;
}

.p-button {
  justify-content: left;
}

.small {
  color: var(--50, #939598);
  text-align: center;

  /* Small Print */
  font-family: Indivisible;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.eventform {
  text-transform: uppercase;
  opacity: 0.75;
  border-top: 1px solid #ccc;
}

@media (max-width: 930px) and (min-width: 801px) {
  .first {
    padding-left: var(--XS, 16px);
  }
  .second {
    padding-left: var(--XS, 16px);
  }
}

@media only screen and (max-width: 800px) {
  .flex-row,
  #app .flex-row {
    display: inline-grid;
    width: 100%;
  }
  .first {
    padding-left: var(--md, 32px);
  }
  .second {
    margin: var(--md, 32px) 0 0 var(--md, 32px);
    padding: 0;
  }
  .price-cell.pa-p1 {
    padding: 0px var(--2XS, 8px);
  }

  .image-cell {
    width: 100%;
    grid-column: 1/1;
    grid-row: span 3;
    border-bottom: none;
    margin: var(--md, 32px) 0 0 0;
  }

  .meta-cell {
    gap: var(--2XS);
    border-bottom: none;
  }
  .meta-cell.flex-space-between {
    justify-content: normal;
  }

  .quantity-cell {
    padding: 0;
    margin: 0;
    border-bottom: 0;
    grid-column: span 2;
  }
  .quantity-cell .flex-row {
    border: 1px solid var(--30, #c4c4c4);
    padding: 0px;
    display: flex !important;
    gap: 0;
    margin: 0 0 var(--Sm, 24px) 0;
    min-width: 92px !important;
  }
  .quantity-cell .flex-row :deep(.p-button-icon) {
    width: 7px;
    height: 7px;
  }

  .p-button.small {
    padding: 0;
  }

  .price-cell {
    border-bottom: 0;
  }

  .cart-items {
    display: grid;
    grid-template-columns: max-content;
  }
  .cart-items .header-price,
  .cart-items .header-quantity {
    display: none;
  }
  .price-cell {
    grid-column: 3/3;
  }
  .quantity-cell {
    grid-column: 2/4;
    a.small {
      text-align: left;
    }
  }
  #app .total-cell {
    padding: 5px 0 var(--md, 32px) 0;
    margin: 0 0 0 var(--md, 32px);
    grid-column: span 2;
    a.small {
      text-align: left;
    }
  }
  #app .total-cell .price {
    display: none;
  }
  #app .quantity-cell {
    width: 94px;
    padding: var(--Sm, 24px) 0 0 var(--md, 32px);
  }
}

@media screen and (max-width: 600px) {
  .image-cell img {
    width: 98px;
    height: 123px;
  }
  .header-total {
    text-align: right;
  }
  .price-cell.pa-p1 {
    text-align: right;
  }
}
</style>
